// Cloudinary configuration for media uploads
// Get your credentials from: https://console.cloudinary.com/

export const getCloudinaryConfig = () => {
  // In production, these should be environment variables
  // For now, we'll use a fallback that requires setting up
  const cloudName = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME || '';
  const uploadPreset = import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET || '';
  
  if (!cloudName || !uploadPreset) {
    console.warn('Cloudinary not configured. Please set VITE_CLOUDINARY_CLOUD_NAME and VITE_CLOUDINARY_UPLOAD_PRESET');
    return null;
  }
  
  return {
    cloudName,
    uploadPreset
  };
};

// Upload file to Cloudinary
export const uploadToCloudinary = async (file, folder = 'media') => {
  const config = getCloudinaryConfig();
  if (!config) {
    throw new Error('Cloudinary not configured. Please set VITE_CLOUDINARY_CLOUD_NAME and VITE_CLOUDINARY_UPLOAD_PRESET environment variables. See CLOUDINARY_SETUP.md for instructions.');
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', config.uploadPreset);
  formData.append('folder', folder);
  
  // Determine resource type
  const isVideo = file.type.startsWith('video/');
  const resourceType = isVideo ? 'video' : 'image';
  
  // Note: 'eager' parameter is not allowed with unsigned uploads (upload_preset)
  // Cloudinary will auto-optimize images automatically, and we can apply
  // transformations via URL parameters when displaying if needed
  if (isVideo) {
    formData.append('resource_type', 'video');
  }

  try {
    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${config.cloudName}/${resourceType}/upload`,
      {
        method: 'POST',
        body: formData
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.error?.message || `Upload failed with status ${response.status}`;
      throw new Error(errorMessage);
    }

    const data = await response.json();
    
    // Return Cloudinary URL
    // Note: For unsigned uploads, eager transformations are not available
    // Images are auto-optimized by Cloudinary, and we can apply transformations
    // via URL parameters when displaying if needed (e.g., ?q_auto,f_auto)
    return {
      url: data.secure_url,
      publicId: data.public_id,
      width: data.width || null,
      height: data.height || null,
      format: data.format,
      bytes: data.bytes,
      // For videos, use the secure URL (optimizations can be applied via URL transformations)
      videoUrl: isVideo ? data.secure_url : undefined
    };
  } catch (error) {
    console.error('Cloudinary upload error:', error);
    // Re-throw with helpful message
    if (error.message.includes('not configured')) {
      throw error;
    }
    throw new Error(`Cloudinary upload failed: ${error.message}`);
  }
};

// Normalize image URL for display
// Ensures URLs use HTTPS and are properly formatted
export const normalizeImageUrl = (url) => {
  if (!url) return url;
  
  // Ensure HTTPS for all URLs (replace http:// with https://)
  if (url.startsWith('http://')) {
    return url.replace('http://', 'https://');
  }
  
  // Return URL as-is if it's already HTTPS or a data URL
  return url;
};

// Delete file from Cloudinary
export const deleteFromCloudinary = async (publicId, resourceType = 'image') => {
  const config = getCloudinaryConfig();
  if (!config) {
    throw new Error('Cloudinary not configured');
  }

  // Note: This requires server-side implementation with API secret
  // For client-side, we'll rely on Cloudinary's auto-cleanup or manual deletion
  console.warn('Client-side deletion not supported. Use server-side function or Cloudinary dashboard.');
  return { success: false, message: 'Use server-side deletion' };
};

